import { useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import type { CompanionState, PointData } from '../types/companion'
import { mousePosition as globalMouse } from '../core/MouseTracker'

/** Offset from the real mouse cursor (px) */
const OFFSET_X = 30
const OFFSET_Y = 35

/** How quickly the cursor follows the mouse (0–1, higher = snappier) */
const FOLLOW_LERP = 0.25

/** Duration of the fly-to / fly-back animation (ms) */
const FLY_DURATION_MS = 600

/** Bezier arc height as fraction of the travel distance */
const ARC_HEIGHT = 0.3

type CursorMode = 'following' | 'flying-to' | 'holding' | 'flying-back'

// Ease-out cubic
function ease(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

// Quadratic bezier with perpendicular arc
function bezierArc(start: { x: number; y: number }, end: { x: number; y: number }, t: number) {
  const midX = (start.x + end.x) / 2
  const midY = (start.y + end.y) / 2
  const dx = end.x - start.x
  const dy = end.y - start.y
  const cpX = midX - dy * ARC_HEIGHT
  const cpY = midY + dx * ARC_HEIGHT
  const s = 1 - t
  return {
    x: s * s * start.x + 2 * s * t * cpX + t * t * end.x,
    y: s * s * start.y + 2 * s * t * cpY + t * t * end.y,
  }
}

/** Per-bar height multipliers to create a natural waveform shape */
const BAR_WEIGHTS = [0.5, 0.8, 1.0, 0.7, 0.4]

interface BuddyCursorProps {
  state: CompanionState
  point: PointData | null
  captureSize?: { width: number; height: number }
  /** Mic audio level 0–1 (only while listening) */
  audioLevel?: number
  /** Called when user clicks (cursor flies back, but speech continues) */
  onCursorDismiss?: () => void
}

/**
 * The cursor's accent. It tinted amber in Build and carried an Ask/Do label
 * until Build went away (2026-10-16): there is one assistant, so one color.
 */
const ACCENT = '#5b8aff'

export function BuddyCursor({
  state,
  point,
  captureSize,
  audioLevel = 0,
  onCursorDismiss,
}: BuddyCursorProps) {
  const cursorRef = useRef<HTMLDivElement>(null)
  const posRef = useRef({
    x: globalMouse.x + OFFSET_X,
    y: globalMouse.y + OFFSET_Y,
  })
  const modeRef = useRef<CursorMode>('following')
  const animFrameRef = useRef(0)
  const flyStartRef = useRef({ x: 0, y: 0 })
  const flyEndRef = useRef({ x: 0, y: 0 })
  const flyStartTimeRef = useRef(0)
  const arrowRef = useRef<SVGSVGElement>(null)
  const labelRef = useRef<HTMLDivElement>(null)

  const onCursorDismissRef = useRef(onCursorDismiss)
  useLayoutEffect(() => {
    onCursorDismissRef.current = onCursorDismiss
  })

  // ── Coordinate mapping ─────────────────────────────────────
  function pointToViewport(p: PointData) {
    const cw = captureSize?.width ?? 1280
    const ch = captureSize?.height ?? 720
    return {
      x: (p.x / cw) * window.innerWidth,
      y: (p.y / ch) * window.innerHeight,
    }
  }

  // ── Cursor-only dismiss (fly back, don't stop speech) ──────
  const dismissCursor = useCallback(() => {
    if (modeRef.current !== 'holding' && modeRef.current !== 'flying-to') return

    flyStartRef.current = { ...posRef.current }
    flyEndRef.current = {
      x: globalMouse.x + OFFSET_X,
      y: globalMouse.y + OFFSET_Y,
    }
    flyStartTimeRef.current = performance.now()
    modeRef.current = 'flying-back'
    onCursorDismissRef.current?.()
  }, [])

  // ── Click dismisses cursor only (not speech) ───────────────
  useEffect(() => {
    const handleClick = () => dismissCursor()

    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [dismissCursor])

  // ── Handle point changes (trigger fly animation) ───────────
  useEffect(() => {
    if (!point) return

    const target = pointToViewport(point)
    flyStartRef.current = { ...posRef.current }
    flyEndRef.current = target
    flyStartTimeRef.current = performance.now()
    modeRef.current = 'flying-to'
  }, [point])

  // ── Main animation loop ────────────────────────────────────
  useEffect(() => {
    const tick = (now: number) => {
      const mode = modeRef.current
      const el = cursorRef.current
      if (!el) {
        animFrameRef.current = requestAnimationFrame(tick)
        return
      }

      if (mode === 'following') {
        const targetX = globalMouse.x + OFFSET_X
        const targetY = globalMouse.y + OFFSET_Y
        posRef.current.x += (targetX - posRef.current.x) * FOLLOW_LERP
        posRef.current.y += (targetY - posRef.current.y) * FOLLOW_LERP
      } else if (mode === 'flying-to') {
        const elapsed = now - flyStartTimeRef.current
        const t = Math.min(elapsed / FLY_DURATION_MS, 1)
        const p = bezierArc(flyStartRef.current, flyEndRef.current, ease(t))
        posRef.current.x = p.x
        posRef.current.y = p.y

        if (t >= 1) {
          modeRef.current = 'holding'
        }
      } else if (mode === 'flying-back') {
        const elapsed = now - flyStartTimeRef.current
        const t = Math.min(elapsed / FLY_DURATION_MS, 1)
        const p = bezierArc(flyStartRef.current, flyEndRef.current, ease(t))
        posRef.current.x = p.x
        posRef.current.y = p.y

        if (t >= 1) {
          modeRef.current = 'following'
        }
      }

      el.style.transform = `translate(${posRef.current.x}px, ${posRef.current.y}px)`

      // Toggle pulse animation and label based on cursor mode
      const isPointing = mode === 'holding' || mode === 'flying-to'
      if (arrowRef.current) {
        arrowRef.current.style.animation =
          mode === 'holding' ? 'clicky-point-pulse 1.2s ease-in-out infinite' : 'none'
      }
      if (labelRef.current) {
        labelRef.current.style.display = isPointing ? 'block' : 'none'
      }

      animFrameRef.current = requestAnimationFrame(tick)
    }

    animFrameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(animFrameRef.current)
  }, [])

  return (
    <div
      ref={cursorRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 10000,
        willChange: 'transform',
      }}
    >
      {state === 'listening' ? (
        /* ── Mic input: reactive audio level bars (blue) ── */
        <div style={{ display: 'flex', alignItems: 'end', gap: 1.5, padding: 2, height: 16 }}>
          {BAR_WEIGHTS.map((weight, i) => {
            const barHeight = Math.max(2, audioLevel * 14 * weight)
            return (
              <div
                key={i}
                style={{
                  width: 2.5,
                  height: barHeight,
                  borderRadius: 1,
                  background: ACCENT,
                  transition: 'height 0.08s ease-out',
                }}
              />
            )
          })}
        </div>
      ) : state === 'processing' ? (
        /* ── Processing: spinning loader ── */
        <div style={{ padding: 2 }}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            style={{ animation: 'clicky-spin 0.8s linear infinite' }}
          >
            <circle
              cx="7"
              cy="7"
              r="5.5"
              fill="none"
              stroke={ACCENT}
              strokeWidth="2"
              strokeDasharray="20 14"
              strokeLinecap="round"
            />
          </svg>
        </div>
      ) : (
        /* ── Idle / Responding / Pointing: the accent triangle ── */
        <svg ref={arrowRef} width="12" height="14" viewBox="0 0 12 14" fill="none">
          <path d="M1 1L11 7L1 13V1Z" fill={ACCENT} />
        </svg>
      )}

      {/* Label when at target — visibility controlled by animation loop */}
      {point?.label && (
        <div
          ref={labelRef}
          style={{
            display: 'none',
            position: 'absolute',
            top: 18,
            left: 0,
            whiteSpace: 'nowrap',
            background: 'var(--clicky-bg, #1a1a2e)',
            border: '1px solid var(--clicky-border, #2a2a4a)',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 12,
            color: 'var(--clicky-text, #e0e0e0)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          {point.label}
        </div>
      )}

      <style>{`
        @keyframes clicky-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes clicky-point-pulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(91,138,255,0)); }
          50% { transform: scale(1.4); filter: drop-shadow(0 0 6px rgba(91,138,255,0.6)); }
        }
        @keyframes clicky-label-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
